import {
    Component,
    EventEmitter,
    OnInit,
    Output,
    Input,
    ElementRef,
    ChangeDetectionStrategy,
    SimpleChange,
    OnChanges,
    ViewChildren,
    QueryList,
    ChangeDetectorRef
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatExpansionPanel } from '@angular/material/expansion';
import { TranslateService } from '@ngx-translate/core';
import { SuggestionFilter } from '../se-smart-text-editor/suggestion-datasource/suggestion-filter';
import { Suggestion, ExtendedSuggestion, User, SaveExtendedSuggestionEvent, CpControlSuggestions, PolicySuggestion } from '../se-smart-text-editor/suggestion-datasource/suggestion';
import { MenuEnum } from '../se-text-editor/menu/menu-items.enum';
import { defaultShortcutList } from '../se-smart-text-editor/shortcuts';
import { cloneDeep, isBoolean, isNil } from 'lodash-es';
import { first } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { CustomTextEditorDialogComponent } from '../custom-text-editor-dialog/custom-text-editor-dialog.component';
import { MatRadioChange } from '@angular/material/radio';

@Component({
    selector: 'se-ui-default-text',
    templateUrl: './se-ui-default-text.component.html',
    styleUrls: ['./se-ui-default-text.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SeUiDefaultTextComponent implements OnInit, OnChanges {

    @ViewChildren("expansionPanel") expPanels: QueryList<MatExpansionPanel>;

    @Input() suggestions: Suggestion[] = [];
    @Input() currentFilter: SuggestionFilter;
    @Input() defaultTextName: string = '';
    @Input() _addingNewText: boolean = false;
    @Input() newTextsGroup: Map<string, CpControlSuggestions> = new Map<string, CpControlSuggestions>();

    @Output() selectedSuggestion = new EventEmitter<ExtendedSuggestion>();
    @Output() preSelectedSuggestionEmitter = new EventEmitter<Suggestion>();
    @Output() closeCommand = new EventEmitter<boolean>();
    @Output() undoCommand = new EventEmitter<boolean>();
    @Output() isEditing = new EventEmitter<boolean>();
    @Output() updateSuggestions = new EventEmitter<SaveExtendedSuggestionEvent>();


    private suggestionText: string = '';
    private suggestionCode: string = '';
    private referringPhysician: User | undefined;
    private idExpandedSuggestion: number;

    preSelectedSuggestion: Suggestion;
    isStandard: boolean = false;

    _originalSuggestion: Suggestion = new Suggestion();
    _selectedSuggestion: Suggestion = new Suggestion();
    _showEditOrDeleteButton = false;

    _currentSelection: Suggestion;
    _editingView = false;
    typeTexts: Array<string> = [];

    // vedi anche TEXT_EDITOR_TOOLBAR_FULL
    _predefinedMenuConfiguration = new Map([
        [MenuEnum.SUGGESTIONS, { visible: true }],
        [MenuEnum.NEW, { visible: false }],
        [MenuEnum.UNDO, { visible: false }],
        [MenuEnum.REDO, { visible: false }],
        [MenuEnum.TEXT_ALIGNMENT, { visible: false }],
        [MenuEnum.TEXT_FONT_WEIGHT, { visible: true }],
        [MenuEnum.TEXT_FONT_STYLE, { visible: true }],
        [MenuEnum.TEXT_DECORATION, { visible: true }],
        [MenuEnum.TEXT_FONT_FAMILY, { visible: true }],
        [MenuEnum.TEXT_FONT_SIZE, { visible: true }],
        [MenuEnum.UNORDERED_LIST, { visible: true }],
        [MenuEnum.ORDERED_LIST, { visible: true }],
        [MenuEnum.MICROPHONE, { visible: false }],
    ]);

    _shortcutList = defaultShortcutList;

    _suggestionPolicyToggleChecked = true;

    isSingleClick: boolean = false;

    constructor(private sanitizer: DomSanitizer,
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private translate: TranslateService,
        private dialog: MatDialog) {
    }

    ngOnInit() {
        if (this.suggestions && this.suggestions.length > 0) {
            this.preSelectedSuggestion = this.suggestions[0];
            this.preSelectedSuggestionEmitter.emit(this.preSelectedSuggestion);
            this.referringPhysician = this.suggestions[0].referringPhysician;
            this.suggestions.sort((a, b) => {

                //standardModalities
                const aStandard = a.standardModalities && a.standardModalities.length > 0 ? 1 : 0;
                const bStandard = b.standardModalities && b.standardModalities.length > 0 ? 1 : 0;
                if (aStandard !== bStandard) {
                    return bStandard - aStandard;
                }

                if (a.isStandard && !b.isStandard) return -1;
                if (b.isStandard && !a.isStandard) return 1;

                
                if (a.standardModules.length === 0 && b.standardModules.length > 0) {
                    return 1;
                }
                if (b.standardModules.length === 0 && a.standardModules.length > 0) {
                    return -1;
                }

                //referringPhysician
                const aPhysician = a.referringPhysician ? 1 : 0;
                const bPhysician = b.referringPhysician ? 1 : 0;
                return bPhysician - aPhysician; // Poi quelli con referringPhysician valorizzato
            });
        }
        this.typeTexts = this.retrieveTypeTexts();
    }

    ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
        for (const propName of Object.keys(changes)) {
            if (propName === 'suggestions') {
                this.preSelectedSuggestion = this.suggestions[0];
                this.preSelectedSuggestionEmitter.emit(this.preSelectedSuggestion);
            }
        }

        if (this._addingNewText) {
            this._onAddClick();
        }
    }

    private retrieveTypeTexts(): Array<string> {
        if (!this.suggestions.length && this.newTextsGroup.size > 0) {
            let typeTexts: string[] = [];
            this.newTextsGroup.forEach((value, key) => {
                typeTexts.push(value.defaultName);
            });
            return typeTexts;
        }
        return Object.getOwnPropertyNames(this.suggestions[0].Sentence);
    }

    _onAddClick() {
        this._showEditOrDeleteButton = false;
        this._currentSelection = new Suggestion();
        this._currentSelection.code = '';
        this._currentSelection.Description = '';
        this._currentSelection.Sentence = {};
        this._editingView = true;
        this.initSelectedSuggestionTexts();
        this._selectedSuggestion = cloneDeep(this._currentSelection);
    }

    private initSelectedSuggestionTexts() {
        this.typeTexts.forEach(text => {
            const cpTextsGroup = this.newTextsGroup.get(text);
            if (cpTextsGroup && text === cpTextsGroup.defaultName) {
                this._currentSelection.Sentence[text] = cpTextsGroup.model
            } else {
                this._currentSelection.Sentence[text] = '';
            }
        });
    }


    preSelectNext() {
        if (this.suggestions.length > 1 && this.preSelectedSuggestion) {
            const newPreSelectedIndex = (this.suggestions.indexOf(this.preSelectedSuggestion) + 1) % this.suggestions.length;
            this.preSelectedSuggestion = this.suggestions[newPreSelectedIndex];
            this.preSelectedSuggestionEmitter.emit(this.preSelectedSuggestion);
            this.elementRef.nativeElement.querySelector('#suggestion-div-' + newPreSelectedIndex).scrollIntoView();
        }
    }

    preSelectPrevious() {
        if (this.suggestions.length > 1 && this.preSelectedSuggestion) {
            let newPreSelectedIndex = this.suggestions.indexOf(this.preSelectedSuggestion) - 1;
            newPreSelectedIndex = newPreSelectedIndex >= 0 ? newPreSelectedIndex : this.suggestions.length - 1;
            this.preSelectedSuggestion = this.suggestions[newPreSelectedIndex];
            this.preSelectedSuggestionEmitter.emit(this.preSelectedSuggestion);
            this.elementRef.nativeElement.querySelector('#suggestion-div-' + newPreSelectedIndex).scrollIntoView();
        }
    }

    getPreselectedSuggestion(): Suggestion {
        return this.preSelectedSuggestion;
    }

    expPanelToggle(event, i, suggestion) {
        event.stopPropagation();
        this.isSingleClick = !this.isSingleClick;
        this.idExpandedSuggestion = i;
        setTimeout(() => {
            if (this.isSingleClick) {
                this.isSingleClick = false;
                if (!isNil(this.expPanels)) {
                    this.expPanels.get(i).toggle();
                }
            }
        }, 250);
    }

    selPanelDoubleClikc(suggestion: Suggestion, event: any) {
        this.isSingleClick = false;
        this.onSelectExpander(suggestion, event);
    }

    onSelectExpander(suggestion: Suggestion, event?: any) {
        event?.stopPropagation();
        if (!this._addingNewText) {
            this.onSelect(suggestion);
        }
    }

    onSelect(suggestion: Suggestion) {
        /* l'invio dell'evento è ritardato per evitare che eventuali doppi click dell'utente siano propagati al background*/
        if (!this._addingNewText) {
            setTimeout(() => {
                this.selectedSuggestion.emit({ suggestion: suggestion, policy: this._suggestionPolicyToggleChecked });
            }, 150);
        }
    }

    onClose() {
        this.closeCommand.emit(true);
    }

    filterCodeAndText(suggestion: Suggestion) {
        const currentFilter: SuggestionFilter = this.currentFilter;
        this.suggestionText = suggestion.Description ? suggestion.Description : '';
        this.suggestionCode = suggestion.code ? suggestion.code : '';
        const currentFilterTextLower = (currentFilter && currentFilter.text) ? currentFilter.text.toLowerCase() : '';
        const currentFilterText = (currentFilter && currentFilter.text) ? currentFilter.text : '';


        if (this.suggestionText.toLowerCase().startsWith(currentFilterTextLower)) {
            this.suggestionText = '<span style=\'color: #e78914;\'>' + this.suggestionText.substring(0, currentFilterText.length) + '</span>' +
                this.suggestionText.substring(currentFilterText.length, this.suggestionText.length);
        }

        if (typeof suggestion.code !== 'undefined' && suggestion.code) {
            this.suggestionCode = suggestion.code;
            if (this.suggestionCode.toLowerCase().startsWith(currentFilterTextLower)) {
                this.suggestionCode = '<span style=\'color: #e78914;\'>' + this.suggestionCode.substring(0, currentFilterText.length) + '</span>'
                    + this.suggestionCode.substring(currentFilterText.length, this.suggestionText.length);
            }
            this.suggestionCode = '[' + this.suggestionCode + ']';
        }
    }

    createSuggestionHTMLExpanderView(suggestion: Suggestion, index: number) {
        this.filterCodeAndText(suggestion);
        return this.sanitizer.bypassSecurityTrustHtml(index + '. ' + this.suggestionCode + ' ' + this.suggestionText);
    }

    _onEditClick() {
        this._showEditOrDeleteButton = false;
        this._originalSuggestion = cloneDeep(this._currentSelection);
        this._selectedSuggestion = cloneDeep(this._currentSelection);

        this._editingView = true;
        this.isEditing.emit(true);
    }

    _onDeleteClick() {
        this.onDeleteAction();
    }

    private onDeleteAction() {
        this.dialog.open(CustomTextEditorDialogComponent, {
            width: '22%',
            height: '20%',
            data: {
                description: 'AreYouSureToDelete',
                title: 'Warning',
                buttonYes: true,
                buttonNo: true
            }
        }).afterClosed().subscribe((result) => {
            if (!!result) {
                this._addingNewText = false;
                this._editingView = false;
                this.updateEvent(false, true);
                this._closedAccordion();
            }
        });
    }

    _undoFunction() {
        this.onUndoAction();
    }

    private onUndoAction() {
        this.dialog.open(CustomTextEditorDialogComponent, {
            width: '22%',
            height: '20%',
            data: {
                description: 'OnUndoMergeExamsWarning',
                title: 'Warning',
                buttonYes: true,
                buttonNo: true
            }
        }).afterClosed().subscribe((result) => {
            if (!!result) {
                this._addingNewText = false;
                this._currentSelection.code = this._originalSuggestion.code;
                this._currentSelection.Description = this._originalSuggestion.Description;
                this._currentSelection.Sentence = this._originalSuggestion.Sentence;

                this._editingView = false;
                this.isEditing.emit(false);
                this._showEditOrDeleteButton = false;
                this.undoCommand.emit(true);
            }
        });
    }

    _okFunction() {
        this.onSaveAction();
    }

    private onSaveAction() {
        this.dialog.open(CustomTextEditorDialogComponent, {
            width: '16%',
            height: '19%',
            minWidth: '300px',
            data: {
                description: 'ConfirmChoose',
                title: 'Warning',
                buttonYes: true,
                buttonNo: true
            }
        }).afterClosed().subscribe((result) => {
            if (!!result) {
                if (this._addingNewText) {
                    this._addingNewText = false;
                    this.createBasicSuggestionForAdd();
                    this.idExpandedSuggestion = 0;
                    if (!this.suggestions.length) {
                        this.addSuggestionToEmptySuggestionsList();
                        this.updateEvent(true, false);
                    } else {
                        this.addSuggestion();
                    }
                } else {
                    this.assigneCodeAndDescription();
                    const updatedSuggestions = [...this.suggestions];
                    updatedSuggestions.unshift(...updatedSuggestions.splice(this.idExpandedSuggestion, 1));
                    this.suggestions = [...updatedSuggestions];
                    this._currentSelection.id = 0;
                    this.updateIdSuggestions();
                    this.updateEvent(false, false);
                    this.handleExpansionAfterAction();
                }
                this._editingView = false;
                this.isEditing.emit(false);
            }
        });
    }

    private createBasicSuggestionForAdd() {
        this._currentSelection.id = 0;
        this._currentSelection.IsNormality = false;
        this._currentSelection.newLineAfter = false;
        this._currentSelection.referringPhysician = this.newTextsGroup.get(this.defaultTextName).referringPhysician;
        this._currentSelection.referringPhysician.id = this.newTextsGroup.get(this.defaultTextName).referringPhysician.id;
        this._currentSelection.groupName = this.newTextsGroup.get(this.defaultTextName).defaultGroup;
        this._currentSelection.modalities = this.suggestions.length ? this.suggestions[0].modalities : [];
        this._currentSelection.standardModalities = [];
        this._currentSelection.modules = this.suggestions.length ? this.suggestions[0].modules : [];
        this._currentSelection.standardModules = this.suggestions.length ? this.suggestions[0].standardModules : [];
        this._currentSelection.groupNameHashCode = this.newTextsGroup.get(this.defaultTextName).groupHashCode;
    }

    private addSuggestionToEmptySuggestionsList() {
        this.assigneCodeAndDescription();
        this.suggestions.push(this._currentSelection);
        this.handleExpansionAfterAction();
        this.cd.detectChanges();
    }

    private updateEvent(isNew: boolean, toDelete: boolean) {
        let updateSaveEvent: SaveExtendedSuggestionEvent = {
            index: toDelete ? this.idExpandedSuggestion : 0,
            suggestions: this.suggestions,
            groupNameHashCode: this.groupNameHashCodeValue(toDelete),
            fromDefaultTextName: this.defaultTextName,
            isNew: isNew,
            toDelete: toDelete
        }
        this.updateSuggestions.emit(updateSaveEvent);
    }

    private groupNameHashCodeValue(toDelete: boolean): number | undefined {
        if (toDelete && this.suggestions[this.idExpandedSuggestion].groupNameHashCode) {
            return this.suggestions[this.idExpandedSuggestion].groupNameHashCode;
        } else {
            return this._currentSelection.groupNameHashCode;
        }
    }

    private addSuggestion() {
        this.assigneCodeAndDescription();
        this.suggestions.unshift(this._currentSelection);
        this.updateIdSuggestions();
        this.updateEvent(true, false);
        this.handleExpansionAfterAction();
    }

    private assigneCodeAndDescription() {
        this._currentSelection.code = this._selectedSuggestion.code;
        this._currentSelection.Description = this._selectedSuggestion.Description;
    }

    private updateIdSuggestions() {
        this.suggestions.forEach((sugg, index) => {
            if (index != 0) {
                ++sugg.id
            }
        });
    }

    private handleExpansionAfterAction() {
        this.preSelectedSuggestion = this._currentSelection;
        this.preSelectedSuggestionEmitter.emit(this.preSelectedSuggestion);
        this._openedAccordion(this._currentSelection, 0);
        this.createSuggestionHTMLExpanderView(this._currentSelection, 0);
        setTimeout(() => {
            const firstExpansionPanel: HTMLElement = document.getElementsByTagName('mat-expansion-panel')[0].querySelector('mat-expansion-panel-header');
            firstExpansionPanel.click();
        }, 0
        );
    }

    private hasText(htmlString: string): boolean {
        const sanitizedHtml = this.sanitizer.sanitize(1, htmlString);
        const text = sanitizedHtml?.replace(/<[^>]*>/g, '').trim();
        return text?.length > 0;
    }

    _isOkButtonDisabled(): boolean {
        let hasContent = Object.keys(this._currentSelection.Sentence).some(property =>
            this.hasText(this._currentSelection.Sentence[property])
        );
        let validity: boolean = !this._selectedSuggestion.Description || !this._selectedSuggestion.code || !hasContent
        return validity;
    }

    _openedAccordion(selectedSuggestion: Suggestion, index: number) {
        this._currentSelection = selectedSuggestion;
        if (!isNil(this.newTextsGroup)) {
            this._showEditOrDeleteButton = this.newTextsGroup.get(this.defaultTextName).referringPhysician.isAuthorized || (!this.newTextsGroup.get(this.defaultTextName).referringPhysician.isAuthorized && !isNil(this._currentSelection.referringPhysician));
        }
        let id = 'suggestion-div-' + index;
        let el = document.getElementById(id);
        if (el) {
            el.scrollIntoView();
        }
    }

    _closedAccordion() {
        this._showEditOrDeleteButton = false;
    }

    public _onSuggestionPolicyChange($event: MatRadioChange) {
        this._suggestionPolicyToggleChecked = $event.value === PolicySuggestion.Concatenate;
    }

    public contentChange(event, key: string) {
        this._currentSelection.Sentence[key] = event.innerHTML;
    }

    _isShared(suggestion: Suggestion): boolean {
        return isNil(suggestion.referringPhysician);
    }

    _isStandard(suggestion: Suggestion): boolean {
        if (suggestion.standardModalities && suggestion.standardModules && isBoolean(suggestion.isStandard)) {
            return suggestion.standardModalities.length > 0 || suggestion.standardModules.length > 0 || suggestion.isStandard;
        }
        return false;
    }

    _getTitle(item: any) {
        if (!isNil(this.newTextsGroup)) {
            const value = this.newTextsGroup.get(item.key);
            if (value && value.label) {
                return value!.label;
            } else {
                return item.key;
            }
        } else {
            return item.key;
        }
    }
}
